import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './OutstandingCallPage.css';

const OutstandingCallPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(4);
  const [filterText, setFilterText] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectedSubtype, setSelectedSubtype] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Clear selections when switching tabs
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    setSelectedItems(new Set());
  };

  // Fetch data from Southeastern API
  useEffect(() => {
    const fetchSoutheasternData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Please login again");
          return;
        }
        
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`${baseUrl}/api/southeastern`, config);
        
        if (response.data.success) {
          setData(response.data.data);
        } else {
          toast.error("Failed to fetch data");
        }
      } catch (error) {
        console.error("Error fetching Southeastern data:", error);
        toast.error("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchSoutheasternData();
  }, []);


  // Filter data based on active tab and search text
  const getFilteredData = () => {
    let tabFilteredData = data;
    
    // Filter by type field - show only "Outstanding Call" type (value 1) for all tabs
    tabFilteredData = data.filter(item => {
      if (!item.type) return false;
      
      // Check if type contains "1" (Outstanding Call)
      const typeValues = item.type.split(',').map(t => t.trim());
      return typeValues.includes('1');
    });
    
    // Additional filtering by tab type based on subtype field
    if (activeTab === 'All') {
      // Keep the type-filtered data (all Outstanding Call data)
      tabFilteredData = tabFilteredData;
    } else if (activeTab === 'STATION CALL OUTS') {
      // Filter by subtype "1" (STATION CALL OUTS)
      tabFilteredData = tabFilteredData.filter(item => 
        item.subtype === '1'
      );
    } else if (activeTab === 'DEPOT CALL OUTS') {
      // Filter by subtype "2" (DEPOT CALL OUTS)
      tabFilteredData = tabFilteredData.filter(item => 
        item.subtype === '2'
      );
    } else if (activeTab === 'PPM FW') {
      // Filter by subtype "3" (PPM FW)
      tabFilteredData = tabFilteredData.filter(item => 
        item.subtype === '3'
      );
    }
    
    // Apply search filter
    if (!filterText) return tabFilteredData;
    
    const searchText = filterText.toLowerCase();
    return tabFilteredData.filter(item => (
      (item.taylorTechnologyNumber && item.taylorTechnologyNumber.toLowerCase().includes(searchText)) ||
      (item.clientReferenceNo && item.clientReferenceNo.toLowerCase().includes(searchText)) ||
      (item.locations && item.locations.toLowerCase().includes(searchText)) ||
      (item.siteDetails && item.siteDetails.toLowerCase().includes(searchText)) ||
      (item.additionalNotes && item.additionalNotes.toLowerCase().includes(searchText)) ||
      (item.priorities && item.priorities.toLowerCase().includes(searchText)) ||
      (item.dateReceived && item.dateReceived.toLowerCase().includes(searchText)) ||
      (item.dateScheduled && item.dateScheduled.toLowerCase().includes(searchText))
    ));
  };

  const filteredData = getFilteredData();

  // Sort filtered data by updatedAt (latest modified first), then by createdAt
  const sortedData = filteredData.sort((a, b) => {
    // First sort by updatedAt (if available)
    if (a.updatedAt && b.updatedAt) {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateB.getTime() - dateA.getTime(); // Descending order (latest first)
      }
    }
    
    // If updatedAt is same or not available, sort by createdAt
    if (a.createdAt && b.createdAt) {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB.getTime() - dateA.getTime(); // Descending order (latest first)
    }
    
    // If no dates available, maintain original order
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const handleSchedule = (item) => {
    // Add schedule functionality here
  };

  // Checkbox handling functions
  const handleSelectAll = () => {
    if (selectedItems.size === currentData.length) {
      // If all items are selected, deselect all
      setSelectedItems(new Set());
    } else {
      // Select all current page items
      const allIds = new Set(currentData.map(item => item._id));
      setSelectedItems(allIds);
    }
  };

  const handleSelectItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  // Check if all items on current page are selected
  const isAllSelected = currentData.length > 0 && currentData.every(item => selectedItems.has(item._id));
  const isIndeterminate = selectedItems.size > 0 && selectedItems.size < currentData.length;

  // Handle subtype submission
  const handleSubtypeSubmit = async () => {
    if (selectedItems.size === 0) {
      toast.error("Select at least one");
      return;
    }
    if (!selectedSubtype) {
      toast.error("Select a subtype");
      return;
    }

    setIsSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login again");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Update each selected item with the subtype
      const updatePromises = Array.from(selectedItems).map(async (itemId) => {
        return axios.put(`${baseUrl}/api/southeastern/${itemId}`, 
          { subtype: selectedSubtype }, 
          config
        );
      });

      await Promise.all(updatePromises);
      
      toast.success(`Updated ${selectedItems.size} items`);
      
      // Clear selections and refresh data
      setSelectedItems(new Set());
      setSelectedSubtype('');
      
      // Refresh data
      const response = await axios.get(`${baseUrl}/api/southeastern`, config);
      if (response.data.success) {
        setData(response.data.data);
      }
      
    } catch (error) {
      console.error("Error updating items:", error);
      toast.error("Failed to update");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="outstanding-call-page no-max-height p-0">
      <div className="table-container">
        {/* Tabs */}
        <div className="tabs-container">
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'All' ? 'active' : ''}`}
              onClick={() => handleTabChange('All')}
            >
              All
            </button>
            <button 
              className={`tab ${activeTab === 'STATION CALL OUTS' ? 'active' : ''}`}
              onClick={() => handleTabChange('STATION CALL OUTS')}
            >
              STATION CALL OUTS
            </button>
            <button 
              className={`tab ${activeTab === 'DEPOT CALL OUTS' ? 'active' : ''}`}
              onClick={() => handleTabChange('DEPOT CALL OUTS')}
            >
              DEPOT CALL OUTS
            </button>
            <button 
              className={`tab ${activeTab === 'PPM FW' ? 'active' : ''}`}
              onClick={() => handleTabChange('PPM FW')}
            >
              PPM FW
            </button>
          </div>
        </div>

        {/* Control Bar */}
        <div className="control-bar">
          {/* <div className="control-left">
            <span>Show</span>
            <select 
              value={itemsPerPage} 
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="items-per-page"
            >
              <option value={4}>4</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>result per page</span>
          </div> */}
          
          <div className="control-right">
            <input
              type="text"
              placeholder="Filter in records..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="filter-input"
            />
            
            {activeTab === 'All' && (
              <>
                <select
                  value={selectedSubtype}
                  onChange={(e) => setSelectedSubtype(e.target.value)}
                  className="filter-input"
                  style={{ marginLeft: '10px', width: '180px' }}
                >
                  <option value="">Select Subtype</option>
                  <option value="1">STATION CALL OUTS</option>
                  <option value="2">DEPOT CALL OUTS</option>
                  <option value="3">PPM FW</option>
                </select>
                
                <button
                  onClick={handleSubtypeSubmit}
                  disabled={isSubmitting || selectedItems.size === 0 || !selectedSubtype}
                  className="schedule-btn"
                  style={{ marginLeft: '10px' }}
                >
                  {isSubmitting ? 'Updating...' : 'Submit'}
            </button>
                
              </>
            )}
          </div>
        </div>

        {/* Data Table */}
        <div className="table-section">
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                {activeTab === 'All' && (
                  <th>
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={input => {
                        if (input) input.indeterminate = isIndeterminate;
                      }}
                      onChange={handleSelectAll}
                      style={{ margin: 0 }}
                    />
                  </th>
                )}
                <th>Date received</th>
                <th>Date scheduled</th>
                <th>Priorities</th>
                <th>TT No.</th>
                <th>Client reference no</th>
                <th>Locations</th>
                <th>Site details</th>
                <th>Additional notes</th>
                
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={activeTab === 'All' ? "10" : "9"} style={{ textAlign: 'center', padding: '20px' }}>
                    Loading Southeastern data...
                  </td>
                </tr>
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan={activeTab === 'All' ? "10" : "9"} style={{ textAlign: 'center', padding: '20px' }}>
                    No Southeastern data found
                    </td>
                  </tr>
                ) : (
                currentData.map((item) => (
                  <tr key={item._id || item.id}>
                    {activeTab === 'All' && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item._id)}
                          onChange={() => handleSelectItem(item._id)}
                          style={{ margin: 0 }}
                        />
                      </td>
                    )}
                    <td>{item.dateReceived || '-'}</td>
                    <td>{item.dateScheduled || '-'}</td>
                    <td>{item.priorities || '-'}</td>
                    <td>{item.taylorTechnologyNumber || '-'}</td>
                    <td>{item.clientReferenceNo || '-'}</td>
                    <td>{item.locations || '-'}</td>
                    <td className="site-details-cell">{item.siteDetails || '-'}</td>
                    <td className="additional-notes-cell">{item.additionalNotes || '-'}</td>
                    
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutstandingCallPage;
